import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  CalendarDays,
  Check,
  Clock3,
  Flame,
  KeyRound,
  LoaderCircle,
  LogOut,
  Mail,
  Palette,
  Pencil,
  Download,
  Trash2,
  ShieldCheck,
  Sparkles,
  Trophy,
  UserRound,
} from 'lucide-react'
import { accountApi } from '../lib/accountApi.js'
import { api } from '../lib/api.js'
import {
  calculateProgress,
  mergeHistory,
  millisecondsUntilNextDaily,
  normalizeRemoteHistory,
  parisDayKey,
  readLocalHistory,
  shiftDay,
} from '../lib/progressStore.js'
import { track } from '../lib/telemetry.js'
import { applySavedTheme, saveTheme } from '../lib/theme.js'

const EMAIL_CODE_LENGTH = 4

function formatDuration(seconds) {
  if (!Number.isFinite(seconds)) return '—'
  const total = Math.max(0, Math.floor(seconds))
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}

function formatHistoryDay(day) {
  return new Intl.DateTimeFormat('fr-FR', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'Europe/Paris',
  }).format(new Date(`${day}T12:00:00.000Z`))
}

function countdownLabel(milliseconds) {
  const total = Math.max(0, Math.ceil(milliseconds / 1000))
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const seconds = total % 60
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

export function NextDailyCountdown({ compact = false }) {
  const [remaining, setRemaining] = useState(() => millisecondsUntilNextDaily())
  useEffect(() => {
    const update = () => setRemaining(millisecondsUntilNextDaily())
    const timer = window.setInterval(update, 1_000)
    return () => window.clearInterval(timer)
  }, [])
  return (
    <div className={`next-daily ${compact ? 'next-daily--compact' : ''}`}>
      <span className="next-daily-icon"><Clock3 size={compact ? 16 : 19} /></span>
      <span><small>Prochaine grille</small><strong>{countdownLabel(remaining)}</strong></span>
    </div>
  )
}

function Calendar({ history }) {
  const completed = useMemo(() => new Map(history.map((entry) => [entry.day, entry])), [history])
  const today = parisDayKey()
  const weekday = new Date(`${today}T12:00:00.000Z`).getUTCDay()
  const daysSinceMonday = (weekday + 6) % 7
  const start = shiftDay(today, -daysSinceMonday - 35)
  const days = Array.from({ length: 42 }, (_, index) => shiftDay(start, index))
  return (
    <div className="streak-calendar">
      <div className="calendar-weekdays" aria-hidden="true">
        {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((label, index) => <span key={`${label}-${index}`}>{label}</span>)}
      </div>
      <div className="calendar-days" aria-label="Vos six dernières semaines">
        {days.map((day) => {
          const entry = completed.get(day)
          const label = `${formatHistoryDay(day)}${entry ? `, terminée en ${formatDuration(entry.elapsedSeconds)}` : ', non terminée'}`
          return (
            <time
              key={day}
              dateTime={day}
              aria-label={label}
              className={`${entry ? 'is-complete' : ''} ${entry?.assisted ? 'is-assisted' : ''} ${day === today ? 'is-today' : ''}`}
            >
              {Number(day.slice(-2))}
            </time>
          )
        })}
      </div>
      <div className="calendar-legend"><span><i /> Terminée</span><span><i className="is-assisted" /> Avec aide</span></div>
    </div>
  )
}

function normalizeAccount(payload) {
  if (!payload) return null
  const account = payload.account || payload.user || (payload.authenticated ? payload : null)
  return account?.registered === false ? null : account
}

function decodeBase64Url(value) {
  const base64 = String(value).replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')
  return Uint8Array.from(atob(padded), (character) => character.charCodeAt(0))
}

function encodeBase64Url(buffer) {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function preparePasskeyOptions(payload, creation) {
  const options = structuredClone(payload?.publicKey || payload?.options || payload)
  options.challenge = decodeBase64Url(options.challenge)
  if (creation) {
    options.user.id = decodeBase64Url(options.user.id)
    options.excludeCredentials = (options.excludeCredentials || []).map((credential) => ({
      ...credential,
      id: decodeBase64Url(credential.id),
    }))
  } else {
    options.allowCredentials = (options.allowCredentials || []).map((credential) => ({
      ...credential,
      id: decodeBase64Url(credential.id),
    }))
  }
  return options
}

function serializeCredential(credential) {
  const response = {
    clientDataJSON: encodeBase64Url(credential.response.clientDataJSON),
  }
  if (credential.response.attestationObject) {
    response.attestationObject = encodeBase64Url(credential.response.attestationObject)
    response.transports = credential.response.getTransports?.() || []
  }
  if (credential.response.authenticatorData) {
    response.authenticatorData = encodeBase64Url(credential.response.authenticatorData)
    response.signature = encodeBase64Url(credential.response.signature)
    response.userHandle = credential.response.userHandle
      ? encodeBase64Url(credential.response.userHandle)
      : null
  }
  return {
    id: credential.id,
    rawId: encodeBase64Url(credential.rawId),
    type: credential.type,
    response,
    clientExtensionResults: credential.getClientExtensionResults?.() || {},
  }
}

function AccountPanel({ nickname, onEditNickname, onAccountChanged, navigate }) {
  const [state, setState] = useState({ loading: true, supported: true, account: null })
  const [theme, setTheme] = useState(applySavedTheme)
  const [step, setStep] = useState('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [challengeId, setChallengeId] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const promptTracked = useRef(false)

  const refresh = useCallback(async () => {
    const payload = await accountApi.account()
    const account = normalizeAccount(payload)
    setState({ loading: false, supported: payload !== null, account })
  }, [])

  useEffect(() => { refresh().catch(() => setState({ loading: false, supported: false, account: null })) }, [refresh])
  useEffect(() => {
    if (!state.loading && !state.account && !promptTracked.current) {
      promptTracked.current = true
      track('account_save_prompt', { source: 'profile' })
    }
  }, [state.account, state.loading])

  const startEmail = async (event) => {
    event.preventDefault()
    const clean = email.trim().toLowerCase()
    if (!/^\S+@\S+\.\S+$/.test(clean)) {
      setMessage('Saisissez une adresse e-mail valide.')
      return
    }
    setBusy(true)
    setMessage('')
    track('account_start', { method: 'email' })
    try {
      let response
      try {
        response = await accountApi.startEmail(clean)
      } catch (error) {
        if (error.status !== 401) throw error
        await api.saveProfile(nickname)
        response = await accountApi.startEmail(clean)
      }
      setEmail(clean)
      setChallengeId(response?.challengeId || response?.challenge_id || '')
      setCode(response?.devCode || '')
      setStep('code')
      setState((current) => ({ ...current, supported: true }))
      setMessage('Un code vient de vous être envoyé.')
    } catch (error) {
      if (error.status === 404 || error.status === 501) setState((current) => ({ ...current, supported: false }))
      setMessage(error.message)
    } finally {
      setBusy(false)
    }
  }

  const confirmEmail = async (event) => {
    event.preventDefault()
    if (!/^\d{4}$/.test(code.trim())) {
      setMessage('Saisissez les 4 chiffres reçus par e-mail.')
      return
    }
    setBusy(true)
    setMessage('')
    try {
      const response = await accountApi.confirmEmail({ email, code: code.trim(), challengeId })
      const account = normalizeAccount(response) || { email }
      setState({ loading: false, supported: true, account })
      setStep('email')
      setCode('')
      track('account_complete', { method: 'email' })
      onAccountChanged?.(account)
      setMessage('Votre progression est maintenant synchronisée.')
    } catch (error) {
      setMessage(error.message)
    } finally {
      setBusy(false)
    }
  }

  const usePasskey = async () => {
    setBusy(true)
    setMessage('')
    const registration = Boolean(state.account)
    track('account_start', { method: 'passkey', action: registration ? 'register' : 'login' })
    try {
      const rawOptions = registration
        ? await accountApi.passkeyRegistrationOptions()
        : await accountApi.passkeyLoginOptions()
      const publicKey = preparePasskeyOptions(rawOptions, registration)
      const credential = registration
        ? await navigator.credentials.create({ publicKey })
        : await navigator.credentials.get({ publicKey })
      const response = registration
        ? await accountApi.verifyPasskeyRegistration(serializeCredential(credential), rawOptions?.challengeId)
        : await accountApi.verifyPasskeyLogin(serializeCredential(credential), rawOptions?.challengeId)
      const account = normalizeAccount(response) || state.account
      setState({ loading: false, supported: true, account })
      track('account_complete', { method: 'passkey', action: registration ? 'register' : 'login' })
      onAccountChanged?.(account)
      setMessage(registration ? 'Clé d’accès ajoutée.' : 'Connexion réussie.')
    } catch (error) {
      if (error?.name !== 'NotAllowedError') setMessage(error.message || 'La clé d’accès n’a pas pu être utilisée.')
    } finally {
      setBusy(false)
    }
  }

  const logout = async () => {
    setBusy(true)
    setMessage('')
    try {
      await accountApi.logout()
      setState((current) => ({ ...current, account: null }))
      onAccountChanged?.(null)
      setMessage('Vous continuez en invité sur cet appareil.')
    } catch (error) {
      setMessage(error.message)
    } finally {
      setBusy(false)
    }
  }

  const exportAccount = async () => {
    setBusy(true)
    setMessage('')
    try {
      const payload = await accountApi.exportAccount()
      const blob = new Blob([JSON.stringify(payload?.export || payload, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `sukoji-export-${new Date().toISOString().slice(0, 10)}.json`
      link.click()
      URL.revokeObjectURL(url)
      setMessage('Votre export a été téléchargé.')
    } catch (error) {
      setMessage(error.message)
    } finally {
      setBusy(false)
    }
  }

  const deleteAccount = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true)
      setMessage('Confirmez une seconde fois pour supprimer définitivement le compte synchronisé.')
      return
    }
    setBusy(true)
    try {
      await accountApi.deleteAccount()
      setState((current) => ({ ...current, account: null }))
      setConfirmDelete(false)
      onAccountChanged?.(null)
      setMessage('Votre compte synchronisé a été supprimé. Le pseudo local reste sur cet appareil.')
    } catch (error) {
      setMessage(error.message)
    } finally {
      setBusy(false)
    }
  }

  const passkeysAvailable = typeof window !== 'undefined'
    && window.isSecureContext
    && 'PublicKeyCredential' in window

  return (
    <section className="account-panel progress-card">
      <div className="progress-card-heading">
        <span className="progress-card-icon"><UserRound size={22} /></span>
        <div><p className="eyebrow">Votre compte</p><h2>{state.account ? 'Progression synchronisée' : 'Vous jouez en invité'}</h2></div>
      </div>
      <div className="local-profile-row">
        <span><strong>{nickname}</strong><small>Pseudo public</small></span>
        <button className="button button--ghost" type="button" onClick={onEditNickname}><Pencil size={16} /> Modifier</button>
      </div>
      <div className="theme-picker theme-picker--profile">
        <span><Palette size={16} /> Thème de l’application</span>
        <div aria-label="Choisir le thème de l’application">
          {[
            ['classic', 'Classique'],
            ['ocean', 'Océan'],
            ['berry', 'Baie'],
          ].map(([id, label]) => (
            <button
              key={id}
              className={`theme-choice theme-choice--${id} ${theme === id ? 'is-active' : ''}`}
              type="button"
              onClick={() => setTheme(saveTheme(id))}
              aria-pressed={theme === id}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {state.loading ? (
        <p className="account-status"><LoaderCircle className="spin" size={17} /> Vérification du compte…</p>
      ) : state.account ? (
        <div className="account-connected">
          <p><ShieldCheck size={20} /><span><strong>{state.account.emailMasked || state.account.email || 'Compte connecté'}</strong><small>Historique disponible sur vos appareils</small></span></p>
          {passkeysAvailable && (
            <button className="button button--ghost button--full" type="button" onClick={usePasskey} disabled={busy}><KeyRound size={17} /> Ajouter une clé d’accès</button>
          )}
          <button className="button button--ghost button--full" type="button" onClick={() => navigate('/archives')}><CalendarDays size={17} /> Ouvrir les archives</button>
          <div className="account-data-actions">
            <button type="button" onClick={exportAccount} disabled={busy}><Download size={16} /> Télécharger mes données</button>
            <button type="button" className="is-danger" onClick={deleteAccount} disabled={busy}><Trash2 size={16} /> {confirmDelete ? 'Confirmer la suppression' : 'Supprimer mon compte'}</button>
          </div>
          <button className="text-link account-logout" type="button" onClick={logout} disabled={busy}><LogOut size={16} /> Se déconnecter</button>
        </div>
      ) : (
        <div className="account-guest">
          <p>Votre progression reste sur ce navigateur. Ajoutez un e-mail pour la retrouver ailleurs.</p>
          {step === 'email' ? (
            <form onSubmit={startEmail}>
              <label htmlFor="account-email">Adresse e-mail</label>
              <div className="account-form-row">
                <input id="account-email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" placeholder="vous@exemple.fr" />
                <button className="button button--primary" type="submit" disabled={busy}><Mail size={17} /> Continuer</button>
              </div>
            </form>
          ) : (
            <form onSubmit={confirmEmail}>
              <label htmlFor="account-code">Code à 4 chiffres envoyé à {email}</label>
              <div className="account-form-row">
                <input id="account-code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{4}" maxLength={EMAIL_CODE_LENGTH} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, EMAIL_CODE_LENGTH))} placeholder="1234" />
                <button className="button button--primary" type="submit" disabled={busy || code.length !== EMAIL_CODE_LENGTH}><Check size={17} /> Valider</button>
              </div>
              <button className="text-link" type="button" onClick={() => { setStep('email'); setMessage('') }}>Changer d’e-mail</button>
            </form>
          )}
          {passkeysAvailable && state.supported && (
            <button className="button button--ghost button--full passkey-button" type="button" onClick={usePasskey} disabled={busy}><KeyRound size={17} /> Utiliser une clé d’accès</button>
          )}
          {!state.supported && <p className="account-unavailable">La synchronisation des comptes n’est pas encore disponible. Votre historique local reste intact.</p>}
        </div>
      )}
      {message && <p className="account-message" role="status">{message}</p>}
    </section>
  )
}

export function PlayerProgressPage({ nickname, onEditNickname, navigate, onAccountChanged }) {
  const [history, setHistory] = useState(readLocalHistory)
  const [remoteStats, setRemoteStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [accountRevision, setAccountRevision] = useState(0)

  useEffect(() => {
    let active = true
    const load = async () => {
      const [statsPayload, historyPayload] = await Promise.all([
        accountApi.stats(),
        accountApi.history(90),
      ])
      if (!active) return
      setRemoteStats(statsPayload?.stats || statsPayload)
      setHistory(mergeHistory(normalizeRemoteHistory(historyPayload), readLocalHistory()))
      setLoading(false)
    }
    load().catch(() => { if (active) { setHistory(readLocalHistory()); setLoading(false) } })
    const onUpdate = () => setHistory((current) => mergeHistory([], [...readLocalHistory(), ...current]))
    window.addEventListener('sukoji:progress-updated', onUpdate)
    return () => { active = false; window.removeEventListener('sukoji:progress-updated', onUpdate) }
  }, [accountRevision])

  const stats = useMemo(() => calculateProgress(history, remoteStats), [history, remoteStats])

  return (
    <main className="page section-shell progress-page">
      <div className="page-heading page-heading--row progress-heading">
        <div><p className="eyebrow">Votre espace</p><h1>Bonjour, {nickname}.</h1><p>Vos séries, vos temps et votre compte réunis au même endroit.</p></div>
        <NextDailyCountdown />
      </div>

      <section className="progress-stats" aria-label="Vos statistiques">
        <article><span><Flame size={19} /> Série actuelle</span><strong>{stats.currentStreak}</strong><small>{stats.currentStreak > 1 ? 'jours' : 'jour'}</small></article>
        <article><span><Trophy size={19} /> Meilleure série</span><strong>{stats.bestStreak}</strong><small>{stats.bestStreak > 1 ? 'jours' : 'jour'}</small></article>
        <article><span><Check size={19} /> Grilles terminées</span><strong>{stats.total}</strong><small>quotidiennes</small></article>
        <article><span><Clock3 size={19} /> Meilleur temps</span><strong>{formatDuration(stats.bestSeconds)}</strong><small>{Number.isFinite(stats.averageSeconds) ? `${stats.averageLabel} ${formatDuration(stats.averageSeconds)}` : '—'}</small></article>
      </section>

      <div className="progress-layout">
        <section className="progress-card calendar-card">
          <div className="progress-card-heading"><span className="progress-card-icon"><CalendarDays size={22} /></span><div><p className="eyebrow">Régularité</p><h2>Six dernières semaines</h2></div></div>
          <Calendar history={history} />
        </section>
        <section className="progress-card history-card">
          <div className="progress-card-heading"><span className="progress-card-icon"><Clock3 size={22} /></span><div><p className="eyebrow">Historique</p><h2>Vos dernières grilles</h2></div></div>
          {loading ? <div className="history-loading"><i /><i /><i /></div> : history.length ? (
            <ol className="history-list">
              {history.slice(0, 12).map((entry) => (
                <li key={entry.day}>
                  <time dateTime={entry.day}>{formatHistoryDay(entry.day)}</time>
                  <span>{entry.assisted ? 'Avec aide' : 'Sans aide'}</span>
                  <strong>{formatDuration(entry.elapsedSeconds)}</strong>
                  {entry.rank && <small>#{entry.rank}</small>}
                </li>
              ))}
            </ol>
          ) : (
            <div className="progress-empty"><Sparkles size={25} /><p>Votre première grille quotidienne apparaîtra ici.</p><button className="button button--primary" type="button" onClick={() => navigate('/jouer/quotidien')}>Jouer aujourd’hui</button></div>
          )}
        </section>
      </div>

      <AccountPanel nickname={nickname} onEditNickname={onEditNickname} onAccountChanged={(account) => {
        setAccountRevision((value) => value + 1)
        onAccountChanged?.(account)
      }} navigate={navigate} />
    </main>
  )
}
